import {
  types, applySnapshot, flow, cast,
  Instance,
} from 'mobx-state-tree';
import { toJS } from 'mobx';

import {
  checkDataType,
  generateNewVisits,
  removeEmptyRows,
  extractParsedDateFormatFamily,
} from '../../../components/orders/UploadStops/helpers/csvTableHelpers';
import { SheetData } from '../../../components/shared/SheetUploader/parser';
import getAppRoot from '../utils/getAppRoot';
import { PatchCsvPreferencesDto, AddOrderDto, AddOrdersMetadataDto } from '../models/RootStore.base';
import { DateFormatPreference } from '../models/DateFormatPreferenceEnum';
import { CSVUploadSteps } from '../../../components/orders/UploadStops/types/CSVTypes';
import getDefaultColumnHeaders from '../../../components/orders/UploadStops/helpers/getColumnHeaders';
import { notifyImportVisitsFailure, notifySheetUploadFailure, notifyOrderAddedSuccess } from '../../../notifications/visits';
import {
  RawSheetData, ColumnHeader, TableRow, ITableRow, IColumnHeader,
} from './models';
import doesRowHaveAddressData from '../../../components/orders/UploadStops/helpers/doesRowHaveAddressData';
import { HeaderNodeTypeEnum, HeaderDataTypeEnum, HeaderColumnKeyEnum } from '../../../components/orders/UploadStops/types/columnHeader';
import selectFromCustomFieldPreferencesDto from '../queries/visits/CustomFieldQuery';
import { TVisitLocationStatus } from '../../../types/TVisit';
import {
  getMatchedColumns,
  getUnmatchedColumns,
  normalizeTime,
  processMatchedColumns,
  processUnmatchedColumns,
} from '../../../components/orders/UploadStops/helpers/ValidateSheetData';
import { GEOCODE_PENDING_MSG_KEY } from '../../../notifications/geocode';
import getAppEnv from '../utils/getAppEnv';
import { withEventTrackingAsync } from '../middlewares/withEventTracking';
import { VALID_EVENT_ACTIONS } from '../../../analytics/eventTrackers';
import { trackUploadOnSuccess } from '../../../analytics/eventTrackers/upload';
import AnalyticsEvents from '../../../analytics/analyticsEvents';
import * as Sentry from '@sentry/browser';
import { notification } from '@routific/hive';
import { CreationSource } from '../models';
import {
  buildDateFormatAnalyticsFromParseResults,
  type CSVDateFormatAnalytics,
  type DateFormatFamily,
} from '../../../components/orders/UploadStops/helpers/csvDateFormatAnalytics';

const Upload = types.model('Upload')
  .props({
    inProgress: types.optional(types.boolean, false),
    rawSheetData: types.maybe(RawSheetData),
    tableData: types.array(TableRow),
    editedTableData: types.array(TableRow),
    savedMappings: types.map(types.map(types.number)), // takes the form:  { [id: string]: { columnKey: string | null, idx: number}}
    tableHeaders: types.array(ColumnHeader),
    manuallyMappedColumns: types.optional(types.array(types.number), []), // distinct CSV column indices the user maps by hand
  })
  .views((self) => ({
    get headers(): IColumnHeader[] {
      return Array.from(self.tableHeaders.values()) || [];
    },
    get numColumnManuallyMapped(): number {
      return self.manuallyMappedColumns.length;
    },
    get enableConfirmModal(): boolean {
      const disabledByUser = JSON.parse(localStorage.getItem('disableReminderModal') ?? 'false');
      return this.totalRowsWithErrors > 0 && !disabledByUser;
    },
    get sheetHeaders(): string[] {
      return self.rawSheetData?.headers ? Array.from(self.rawSheetData.headers.values()) : [];
    },
    get dataSource(): ITableRow[] {
      return toJS(self.tableData);
    },
    get totalRowCount(): number {
      return (Array.from(self.tableData.values()) || []).length;
    },
    get matchedHeaders(): IColumnHeader[] {
      return this.headers.filter((header: IColumnHeader) => header.matched).sort((a: IColumnHeader, b: IColumnHeader) => a.order - b.order);
    },
    get matchedHeadersMap(): { [key: string]: IColumnHeader } {
      return this.matchedHeaders.reduce((acc, header) => {
        acc[header.columnKey] = header;
        return acc;
      }, {} as { [key: string]: IColumnHeader });
    },
    get mappedRequiredLocationHeaders(): IColumnHeader[] {
      return this.matchedHeaders.filter((header: IColumnHeader) =>
        header.columnKey === HeaderColumnKeyEnum.ADDRESS || header.columnKey === HeaderColumnKeyEnum.LATITUDE || header.columnKey === HeaderColumnKeyEnum.LONGITUDE);
    },
    get mappedNonLocationHeaders(): IColumnHeader[] {
      return this.matchedHeaders.filter((header: IColumnHeader) =>
        !this.mappedRequiredLocationHeaders.some((locationHeader: IColumnHeader) => locationHeader.columnKey === header.columnKey));
    },
    get allCSVHeadersMapped(): boolean {
      const mappedHeaders: any[] = [];

      const savedMappings = self.savedMappings.toJSON();
      Object.keys(savedMappings).forEach(columnKey => {
        const currentKey = savedMappings[columnKey];
        Object.keys(currentKey).forEach(item => {
          mappedHeaders.push(item);
        });
      });

      let allMapped = true;
      self.rawSheetData?.headers.forEach(csvHeader => {
        if (mappedHeaders.indexOf(csvHeader.toLowerCase()) === -1) {
          allMapped = false;
        }
      });
      return allMapped;
    },
    get isMissingRequiredHeaders(): boolean {
      return this.headers.some((header: IColumnHeader) => {
        const requiredNotMatched = header.required && !header.matched;
        const notMatchedButDependencyMatched = !header.matched && header.hasMatchedDependency;

        return requiredNotMatched || notMatchedButDependencyMatched;
      });
    },
    get isNotValidImport(): boolean {
      return this.isMissingRequiredHeaders || this.totalCellErrors > 0;
    },
    get mappedLocationMatchedHeaders(): { [key: string]: IColumnHeader } {
      return this.matchedHeaders
        .filter((header: IColumnHeader) => header.nodePath === 'locations')
        .reduce((acc: { [key: string]: IColumnHeader }, header: IColumnHeader) => {
          acc[header.columnKey] = header;
          return acc;
        }, {});
    },
    get mappedLocationFields(): string[] {
      return Object.keys(this.mappedLocationMatchedHeaders);
    },
    get hasMappedLocationFields(): boolean {
      return this.mappedLocationFields?.length > 0;
    },
    get mappedAddressHeader(): IColumnHeader | undefined {
      return this.matchedHeaders.find((header: IColumnHeader) => header.columnKey === HeaderColumnKeyEnum.ADDRESS);
    },
    get preEditErroredRows(): string[] {
      const rowIdxs = this.matchedHeaders.reduce((acc: string[], header: IColumnHeader) => {
        if (header.erroredRows) {
          acc = [...acc, ...header.preEditErroredRows];
        }

        return acc;
      }, []);

      // Use Set to get unique rowKeys only
      return Array.from(new Set(rowIdxs)).sort((a, b) => Number(a) - Number(b)) || null;
    },
    get totalCellErrors(): number {
      return this.matchedHeaders.reduce((acc: number, header: IColumnHeader) => {
        if (header.errorCount > 0) {
          acc += header.errorCount;
        }
        return acc;
      }, 0);
    },
    get tableErroredRowIdxs(): string[] {
      const erroredRowIdxsSet = new Set<string>();

      this.matchedHeaders.forEach((header: IColumnHeader) => {
        header.erroredRowIdxs.forEach((idx: string) => {
          erroredRowIdxsSet.add(idx);
        });
      });

      return Array.from(erroredRowIdxsSet);
    },
    get totalRowsWithErrors(): number {
      return this.tableErroredRowIdxs.length;
    },
    get addressErrorCount(): number {
      return (this.matchedHeadersMap[HeaderColumnKeyEnum.ADDRESS]?.errorCount ?? 0)
        + (this.matchedHeadersMap[HeaderColumnKeyEnum.LATITUDE]?.errorCount ?? 0)
        + (this.matchedHeadersMap[HeaderColumnKeyEnum.LONGITUDE]?.errorCount ?? 0);
    },
    get timeWindowErrorCount(): number {
      return (this.matchedHeadersMap[HeaderColumnKeyEnum.START_TIME]?.errorCount ?? 0)
        + (this.matchedHeadersMap[HeaderColumnKeyEnum.END_TIME]?.errorCount ?? 0);
    },
    get durationErrorCount(): number {
      return this.matchedHeadersMap[HeaderColumnKeyEnum.DURATION]?.errorCount ?? 0;
    },
    get deliveryDateErrorCount(): number {
      return this.matchedHeadersMap[HeaderColumnKeyEnum.DELIVERY_DATE]?.errorCount ?? 0;
    },
    get phoneNumberErrorCount(): number {
      return this.matchedHeadersMap[HeaderColumnKeyEnum.PHONE]?.errorCount ?? 0;
    },
    get totalValidRows(): number {
      return this.totalRowCount - this.totalRowsWithErrors;
    },
    get rowsWithAddressData(): ITableRow[] {
      const { latitude, longitude, ...mappedAddressHeaders } = this.mappedLocationMatchedHeaders;
      const tableData = self.editedTableData.length ? self.editedTableData : self.tableData;
      return tableData.filter((r) => doesRowHaveAddressData(r, mappedAddressHeaders));
    },
    get filteredTableData(): ITableRow[] {
      const dataToFilter = self.editedTableData.length ? self.editedTableData : self.tableData;

      return dataToFilter.filter((_, i) => !this.tableErroredRowIdxs.includes(i.toString()));
    },
  }))
  .actions(self => ({
    reset: () => {
      applySnapshot(self, {});
    },
    addManuallyMappedColumn(columnIdx: number) {
      if (!self.manuallyMappedColumns.includes(columnIdx)) {
        self.manuallyMappedColumns.push(columnIdx);
      }
    },
    removeManuallyMappedColumn(columnIdx: number) {
      const index = self.manuallyMappedColumns.indexOf(columnIdx);
      if (index !== -1) {
        self.manuallyMappedColumns.splice(index, 1);
      }
    },
    initializeTableHeaders: flow(function* initializeTableHeaders() {
      const result = yield getAppRoot(self).model.getCustomFieldKeys();
      self.tableHeaders = cast(getDefaultColumnHeaders(result.customFields.customFields));
    }),
  }))
  .actions((self) => ({
    mapColumn(columnKey: string, columnIdx: number) {
      // Owns the header mutation and the manual-mapping count together so they can't drift.
      // Dedup by column index, and drop the vacated column when a field is reassigned or cleared,
      // so numColumnManuallyMapped never over-counts (gitar review).
      if (columnKey) {
        self.addManuallyMappedColumn(columnIdx);
      } else {
        self.removeManuallyMappedColumn(columnIdx);
      }

      self.headers.forEach((header: IColumnHeader) => {
        if (!columnKey && header.matchedIndex === columnIdx) {
          header.mapToIndex(-1);
          return;
        }

        if (header.columnKey === columnKey) {
          // Field is moving off its previous column -> that column is no longer mapped.
          if (header.matchedIndex !== -1 && header.matchedIndex !== columnIdx) {
            self.removeManuallyMappedColumn(header.matchedIndex);
          }
          header.mapToIndex(-1); // keep Select values unique
          header.mapToIndex(columnIdx);
        } else if (header.matchedIndex === columnIdx) {
          header.mapToIndex(-1);
        }
      });
    },
  }))
  .actions((self) => ({
    detectAndTrackDateFormatAnalytics() {
      const deliveryDateHeader = self.matchedHeaders.find((h: IColumnHeader) => h.columnKey === HeaderColumnKeyEnum.DELIVERY_DATE && h.matched);

      if (deliveryDateHeader) {
        const detectedFormat = deliveryDateHeader.detectedDateFormatFamily;

        if (detectedFormat) {
          // Parse each date and track which format family was actually used
          const parsedDateFormatFamilies: (DateFormatFamily | null)[] = self.filteredTableData.map((row) => {
            const cellValue = row.cells?.[deliveryDateHeader.matchedIndex]?.value;
            if (!cellValue) return null;

            // Extract the format family that was actually used to parse this date
            return extractParsedDateFormatFamily(cellValue, detectedFormat);
          });

          const dateFormatAnalytics: CSVDateFormatAnalytics = buildDateFormatAnalyticsFromParseResults(
            parsedDateFormatFamilies.filter((f): f is DateFormatFamily => f !== null),
            self.deliveryDateErrorCount,
            detectedFormat,
            getAppRoot(self).organization.settings?.dateFormatPreference as DateFormatPreference | undefined || DateFormatPreference.AUTO,
          );

          if (dateFormatAnalytics) {
            getAppRoot(self).account.trackUserForAnalytics(
              AnalyticsEvents.CSV_DATE_FORMAT_DETECTED,
              dateFormatAnalytics,
            );
          }
        }
      }
    },
    addTableHeader(tableHeader: IColumnHeader) {
      self.tableHeaders.push(tableHeader);
    },
    showOrderAddedToastNotification(newOrders: AddOrderDto[]) {
      // Find earliest delivery date in a single pass
      const earliestDeliveryDate = newOrders.reduce<string | undefined>((earliest, order) => {
        if (!order.deliveryDate) return earliest;
        if (!earliest) return order.deliveryDate;
        return order.deliveryDate < earliest ? order.deliveryDate : earliest;
      }, undefined);

      // Create and show notification if conditions are met
      if (earliestDeliveryDate && getAppRoot(self).ui.selectedDate && earliestDeliveryDate !== getAppRoot(self).ui.selectedDate) {
        notification(notifyOrderAddedSuccess(
          newOrders.length,
          earliestDeliveryDate,
          () => getAppRoot(self).router.redirectToDate(earliestDeliveryDate),
        ));
      }
    },
  }))
  .actions((self) => ({
    addCustomField: flow(function* addCustomField(newField: string) {
      const projectId = getAppRoot(self).ui.selectedWorkspace?.id;
      if (projectId != null) {
        const result = yield getAppRoot(self).model.mutateUpsertCustomFields({
          customFieldPrefs: {
            customFields: [newField],
          },
        }, selectFromCustomFieldPreferencesDto).currentPromise();
        if (result.upsertCustomFields.customFields && result.upsertCustomFields.customFields.includes(newField)) {
          self.addTableHeader({
            columnKey: newField,
            matchKeys: cast([]),
            order: self.tableHeaders.length,
            label: newField,
            nodePath: HeaderColumnKeyEnum.CUSTOM_FIELDS,
            nodeType: HeaderNodeTypeEnum.OBJECT,
            dataType: HeaderDataTypeEnum.STRING,
            isLocationHeader: false,
          } as IColumnHeader);
        }
      }
    }),
    setInProgress(active: boolean) {
      self.inProgress = active;
    },
    setRawSheetData(data: SheetData) {
      const headers = data?.header || [];
      const content = removeEmptyRows(data?.content);
      self.rawSheetData = RawSheetData.create({
        headers,
        content,
        extension: data.fileExtension,
      });
    },
    setEditedTableData(editedTableData: ITableRow[]) {
      self.editedTableData = editedTableData as any;
    },
    addTableRow(item: ITableRow) {
      self.tableData.push(item);
    },
    fetchCSVMappings: flow(function* (keys: string[]) {
      const response = yield getAppRoot(self).model.queryCsvMappings({ keys }, qb => qb.newKeys.mapping, { fetchPolicy: 'no-cache' }).currentPromise();
      self.savedMappings = response.csvMappings.mapping;
      return response;
    }),
    patchCSVMappings: flow(function* (newMapping: PatchCsvPreferencesDto['mapping']) {
      const response = yield getAppRoot(self).model.mutateUpsertCsvMappings({ csvPreferences: { mapping: newMapping } }, qb => qb.newKeys.mapping).currentPromise();
      self.savedMappings = response.upsertCsvMappings.mapping;
      return response;
    }),
    prepareTableRowsForUpload() {
      const addressHeaderIndex = self.mappedAddressHeader?.matchedIndex;

      if (addressHeaderIndex !== undefined) {
        self.rowsWithAddressData.forEach((tableRow) => {
          const rowAddress = tableRow.cells?.[addressHeaderIndex]?.value;
          tableRow.updateLocation({
            formattedAddress: rowAddress,
            locationStatusId: TVisitLocationStatus.GEOCODING,
          });
        });
      }
    },
    createVisitsFromUploadData: flow(function* () {
      try {
        getAppRoot(self).upload.prepareTableRowsForUpload();
        const newOrders = generateNewVisits(
          self.filteredTableData,
          self.matchedHeaders,
          getAppRoot(self).ui.geolocation?.countryCode,
        );
        const { createNewOrders, fetchUnscheduledVisitsLatLngs } = getAppRoot(self).model;
        const { setShowImportDialog } = getAppRoot(self).ui;
        const metadata: AddOrdersMetadataDto = {
          creationSource: CreationSource.CSV,
        };

        yield createNewOrders(newOrders, metadata);
        // ORDERS_CREATED analytics is fired server-side by planning-service (it already carries
        // creationSource), so we deliberately don't fire it here — avoids the duplicate
        // same-timestamp event Jenya flagged (ROUT-1976).

        setShowImportDialog(false, CSVUploadSteps.MAP);
        const selectedWorkspaceId = getAppRoot(self).ui.selectedWorkspace?.id;
        if (selectedWorkspaceId) {
          // TODO: remove once we've added back the upload csv added visits subscription handler,
          // at which point we can refresh only the visits in the table
          fetchUnscheduledVisitsLatLngs(selectedWorkspaceId);
          getAppRoot(self).model.queryProjectById({ projectId: selectedWorkspaceId }, q => q.visitGeocodingCount, { fetchPolicy: 'no-cache' });
          getAppRoot(self).upload.setInProgress(false);
          getAppRoot(self).ui.setShowUploadDropzone(false);

          // Show success toast notification
          getAppRoot(self).upload.showOrderAddedToastNotification(newOrders);

          getAppRoot(self).upload.reset();
        }
      } catch (err) {
        // Import failures are tracked in Sentry, not the analytics funnel (ROUT-1976).
        Sentry.captureException(err);
        notifyImportVisitsFailure();
        getAppEnv(self).closeMessage(GEOCODE_PENDING_MSG_KEY);
        self.reset();
      }
    }),
    onSuccess: flow(function* (data: SheetData): Generator<Promise<any>, void, unknown> {
      try {
        getAppRoot(self).upload.setRawSheetData(data);
        yield getAppRoot(self).upload.validateSheetData();
        // Emit ORDER_FILE_UPLOADED before opening any dialog: setShowImportDialog fires
        // MODAL_VIEWED synchronously, so it must come second for the funnel to order the steps (ROUT-1976).
        // Isolate tracking so an analytics failure can't fall through to the catch and abort the import
        // (withEventTrackingAsync previously provided this isolation).
        try {
          trackUploadOnSuccess();
        } catch (trackErr) {
          console.error('Failed to track ORDER_FILE_UPLOADED', trackErr);
        }
        if (!self.hasMappedLocationFields || !self.allCSVHeadersMapped) {
          // No address fields mapped or csv contains headers that have not been mapped before.
          getAppRoot(self).ui.setShowImportDialog(true, CSVUploadSteps.MAP);
        } else if (self.hasMappedLocationFields) {
          if (!self.isMissingRequiredHeaders && self.totalCellErrors > 0) {
            // Location mapped but has errors
            getAppRoot(self).ui.setShowImportDialog(true, CSVUploadSteps.CONFIRM);
          } else if (self.isMissingRequiredHeaders) {
            // Location field mapped but has no errors
            // but missing another required field
            getAppRoot(self).ui.setShowImportDialog(true, CSVUploadSteps.MAP);
          } else {
            getAppRoot(self).upload.createVisitsFromUploadData();
          }
        }
      } catch (err) {
        console.log(err);
        self.reset();
      }
    }),
    validateSheetData: flow(function* (): Generator<Promise<any>, void, unknown> {
      getAppRoot(self).upload.setupContent();
      yield getAppRoot(self).upload.setupHeaders();
      getAppRoot(self).upload.detectAndTrackDateFormatAnalytics();
    }),
    onError: withEventTrackingAsync(self, VALID_EVENT_ACTIONS.UPLOAD_ON_ERROR,
      function (err?: { name: string; message: string }) {
        if (err) {
          notifySheetUploadFailure(err);
          getAppRoot(self).upload.setInProgress(false);
          self.reset();
        }
      }),
    setupContent() {
      const { upload } = getAppRoot(self);
      const headers = upload.rawSheetData?.headers;
      const rows = upload.rawSheetData?.content;

      rows?.forEach((rowData: (string | undefined)[], rowIdx: number) => {
        const rowItem: any = {
          key: rowIdx,
          location: null,
          cells: [],
        };
        headers?.forEach((sheetHeader: string, columnIdx: number) => {
          let cellValue = rowData[columnIdx];

          if (sheetHeader === 'Time Window Start' || sheetHeader === 'Time Window End') {
            cellValue = normalizeTime(cellValue);
          }

          rowItem.cells[columnIdx] = {
            key: `row-${rowIdx}-column-${columnIdx}`,
            columnIdx,
            rowIdx,
            sheetHeader,
            value: cellValue,
            type: checkDataType(cellValue),
          };
        });
        upload.addTableRow(rowItem);
      });
    },
    setupHeaders: flow(function* (): Generator<Promise<any>, void, unknown> {

      const { upload } = getAppRoot(self);
      yield upload.initializeTableHeaders();
      const currentUploadHeaders = upload.rawSheetData?.headers.map((header: string) => header.toLowerCase());

      if (currentUploadHeaders) {
        const availableMappings: any = yield upload.fetchCSVMappings(currentUploadHeaders);

        const { mapping } = availableMappings.csvMappings;
        const { headers } = upload;

        // columns that have matching column name and index in mapping
        const matchedColumns = getMatchedColumns(currentUploadHeaders, mapping);
        processMatchedColumns(matchedColumns, currentUploadHeaders, mapping, headers);

        // handles two cases:
        // 1. columns that have a matching column name in mapping but not the same column index
        // 2. columns that do not exists in mapping at all
        const unmatchedColumns = getUnmatchedColumns(currentUploadHeaders, matchedColumns);
        processUnmatchedColumns(unmatchedColumns, currentUploadHeaders, mapping, headers);

      }
    }),
  }));

export default Upload;
export interface IUploadStore extends Instance<typeof Upload> { }
